import {
	ExportRowData,
	ExportSummary,
	ExportHeaderMetadata,
	getPaymentTypeText,
} from "../../lib/utils/export-utils";
import "../fonts.css";

export interface DebitorenExportTemplateProps {
	exportData: ExportRowData[];
	summary: ExportSummary;
	headerMetadata?: ExportHeaderMetadata;
}

export const DebitorenExportTemplate: React.FC<
	DebitorenExportTemplateProps
> = ({ exportData, summary, headerMetadata }) => {
	const formatAmount = (amount: number): string =>
		amount.toFixed(2).replace(".", ",");

	const isPaymentJournal = headerMetadata?.exportType === "zahlungsjournal";

	// Determine if we have multiple row types (need to show Typ column)
	const hasInvoices = summary.invoiceCount > 0;
	const hasPayments = summary.paymentCount > 0;
	const hasLoss = summary.lossCount > 0;

	// Show Typ column only when there are multiple row types
	const showTypColumn = isPaymentJournal
		? hasInvoices || hasLoss // Payment journal: show if invoices or loss exist alongside payments
		: hasPayments || hasLoss; // Invoice journal: show if payments or loss exist alongside invoices

	// Show VAT columns only when there are invoices (payments don't have VAT breakdown)
	const showVatColumns = hasInvoices || !isPaymentJournal;

	// Show Nummer column only when there are invoices (for payment journal without invoices, hide it)
	const showNummerColumn = hasInvoices || !isPaymentJournal;

	const th = {
		borderBottom: "1px solid #999",
		padding: "2px 3px",
		textAlign: "left" as const,
		fontWeight: "bold" as const,
		fontSize: "10px",
		backgroundColor: "#f5f5f5",
	};
	const thRight = { ...th, textAlign: "right" as const };
	const td = {
		borderBottom: "1px solid #ddd",
		padding: "5px 5px",
		fontSize: "10px",
		fontWeight: 400,
	};
	const tdRight = { ...td, textAlign: "right" as const };

	// Colors: main rows get gray, sub-rows get white
	const mainRowBg = "#d1d5db"; // medium gray
	const subRowBg = "#ffffff";

	// For payment journal: payments are main rows (gray), invoices are sub-rows (white)
	// For invoice journal: invoices are main rows (gray), payments are sub-rows (white)
	const invoiceBg = isPaymentJournal
		? subRowBg
		: showTypColumn
			? mainRowBg
			: subRowBg;
	const paymentBg = isPaymentJournal
		? showTypColumn
			? mainRowBg
			: subRowBg
		: subRowBg;
	const lossBg = subRowBg;

	// Calculate column count based on visible columns
	let columnCount = 6; // Base: Datum, Empfänger, Kunde, Status, Betrag, Währung
	if (showTypColumn) columnCount++;
	if (showNummerColumn) columnCount++;
	if (showVatColumns) columnCount += 3; // MwSt.-Satz, Netto, MwSt.-Betrag

	return (
		<div
			style={{
				fontFamily: "LiberationSans, sans-serif",
				fontSize: "10px",
				padding: "15px 40px",
				minHeight: "277mm",
				boxSizing: "border-box",
			}}
		>
			<style>{`
				thead { display: table-header-group; }
				tbody { display: table-row-group; }
				tfoot { display: table-footer-group; }
			`}</style>

			<table
				style={{
					borderCollapse: "collapse",
					width: "100%",
					tableLayout: "fixed",
					backgroundColor: "white",
				}}
			>
				<colgroup>
					{showTypColumn && <col style={{ width: "9%" }} />}
					{showNummerColumn && (
						<col style={{ width: showTypColumn ? "8%" : "10%" }} />
					)}
					{/* Datum */}
					<col
						style={{
							width: showNummerColumn
								? showTypColumn
									? "8%"
									: "10%"
								: "12%",
						}}
					/>
					{/* Empfänger */}
					<col
						style={{
							width: showNummerColumn
								? showTypColumn
									? "14%"
									: "16%"
								: "22%",
						}}
					/>
					{/* Kunde */}
					<col
						style={{
							width: showNummerColumn
								? showTypColumn
									? "14%"
									: "16%"
								: "22%",
						}}
					/>
					{/* Zahlungsart/Status */}
					<col style={{ width: showNummerColumn ? "8%" : "14%" }} />
					{showVatColumns && (
						<>
							<col style={{ width: "5%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "9%" }} />
						</>
					)}
					{/* Betrag */}
					<col
						style={{
							width: showVatColumns
								? "10%"
								: showNummerColumn
									? "18%"
									: "20%",
						}}
					/>
					{/* Währung */}
					<col style={{ width: showNummerColumn ? "4%" : "6%" }} />
				</colgroup>
				<thead>
					<tr>
						{showTypColumn && <th style={th}>Typ</th>}
						{showNummerColumn && <th style={th}>Nummer</th>}
						<th style={th}>Datum</th>
						<th style={th}>Empfänger</th>
						<th style={th}>Kunde</th>
						<th style={th}>
							{isPaymentJournal ? "Zahlungsart" : "Status"}
						</th>
						{showVatColumns && (
							<>
								<th style={th}>MwSt.-Satz</th>
								<th style={thRight}>Netto</th>
								<th style={thRight}>MwSt.-Betrag</th>
							</>
						)}
						<th style={thRight}>Betrag</th>
						<th style={th}></th>
					</tr>
				</thead>
				<tbody>
					{exportData.map((row, index) => {
						const isInvoice =
							row.typ === "Rechnung" || row.typ === "Akonto";
						const isLoss = row.typ === "Verlust";
						const rowBg = isInvoice
							? invoiceBg
							: isLoss
								? lossBg
								: paymentBg;
						return (
							<tr key={index} style={{ backgroundColor: rowBg }}>
								{showTypColumn && <td style={td}>{row.typ}</td>}
								{showNummerColumn && (
									<td style={td}>{row.rechnungsnummer}</td>
								)}
								<td style={td}>{row.datum}</td>
								<td style={td}>{row.empfänger}</td>
								<td style={td}>{row.kunde}</td>
								<td style={td}>
									{row.zahlungsfortschritt ||
										getPaymentTypeText(
											row.zahlungstyp || ""
										)}
								</td>
								{showVatColumns && (
									<>
										<td style={td}>{row.mwst_satz}</td>
										<td style={tdRight}>
											{row.netto_betrag}
										</td>
										<td style={tdRight}>
											{row.mwst_betrag}
										</td>
									</>
								)}
								<td style={tdRight}>
									{row.brutto_betrag || row.betrag}
								</td>
								<td style={td}>{row.währung}</td>
							</tr>
						);
					})}
				</tbody>
				<tfoot>
					<tr>
						<td
							colSpan={columnCount}
							style={{ height: "10px", border: "none" }}
						></td>
					</tr>
					{/* For payment journal: show payments summary first, then optional invoices */}
					{/* For invoice journal: show invoices summary first, then optional payments */}
					{isPaymentJournal ? (
						<>
							{hasPayments && (
								<tr
									style={{
										backgroundColor: paymentBg,
										fontWeight: "bold",
									}}
								>
									<td style={td}>Zahlungen</td>
									<td style={td}>
										{summary.paymentCount} Stück
									</td>
									<td style={td}></td>
									<td style={td}></td>
									{showTypColumn && <td style={td}></td>}
									{showNummerColumn && <td style={td}></td>}
									{showVatColumns && (
										<>
											<td style={td}></td>
											<td style={td}></td>
											<td style={td}></td>
										</>
									)}
									<td style={tdRight}>
										{formatAmount(summary.paymentBetrag)}
									</td>
									<td style={td}>{summary.currency}</td>
								</tr>
							)}
							{hasLoss && (
								<tr
									style={{
										backgroundColor: lossBg,
										fontWeight: "bold",
									}}
								>
									<td style={td}>Verlust</td>
									<td style={td}>
										{summary.lossCount} Stück
									</td>
									<td style={td}></td>
									<td style={td}></td>
									{showTypColumn && <td style={td}></td>}
									{showNummerColumn && <td style={td}></td>}
									{showVatColumns && (
										<>
											<td style={td}></td>
											<td style={td}></td>
											<td style={td}></td>
										</>
									)}
									<td style={tdRight}>
										{formatAmount(summary.lossBetrag)}
									</td>
									<td style={td}>{summary.currency}</td>
								</tr>
							)}
						</>
					) : (
						<>
							<tr
								style={{
									backgroundColor: invoiceBg,
									fontWeight: "bold",
								}}
							>
								<td style={td}>Rechnungen</td>
								<td style={td}>{summary.invoiceCount} Stück</td>
								<td style={td}></td>
								<td style={td}></td>
								{showTypColumn && <td style={td}></td>}
								{showNummerColumn && <td style={td}></td>}
								{showVatColumns && (
									<>
										<td style={td}></td>
										<td style={tdRight}>
											{formatAmount(summary.invoiceNetto)}
										</td>
										<td style={tdRight}>
											{formatAmount(summary.invoiceMwst)}
										</td>
									</>
								)}
								<td style={tdRight}>
									{formatAmount(summary.invoiceBrutto)}
								</td>
								<td style={td}>{summary.currency}</td>
							</tr>
							{hasPayments && (
								<tr
									style={{
										backgroundColor: paymentBg,
										fontWeight: "bold",
									}}
								>
									<td style={td}>Zahlungen</td>
									<td style={td}>
										{summary.paymentCount} Stück
									</td>
									<td style={td}></td>
									<td style={td}></td>
									{showTypColumn && <td style={td}></td>}
									{showNummerColumn && <td style={td}></td>}
									{showVatColumns && (
										<>
											<td style={td}></td>
											<td style={td}></td>
											<td style={td}></td>
										</>
									)}
									<td style={tdRight}>
										{formatAmount(summary.paymentBetrag)}
									</td>
									<td style={td}>{summary.currency}</td>
								</tr>
							)}
							{hasLoss && (
								<tr
									style={{
										backgroundColor: lossBg,
										fontWeight: "bold",
									}}
								>
									<td style={td}>Verlust</td>
									<td style={td}>
										{summary.lossCount} Stück
									</td>
									<td style={td}></td>
									<td style={td}></td>
									{showTypColumn && <td style={td}></td>}
									{showNummerColumn && <td style={td}></td>}
									{showVatColumns && (
										<>
											<td style={td}></td>
											<td style={td}></td>
											<td style={td}></td>
										</>
									)}
									<td style={tdRight}>
										{formatAmount(summary.lossBetrag)}
									</td>
									<td style={td}>{summary.currency}</td>
								</tr>
							)}
						</>
					)}
				</tfoot>
			</table>
		</div>
	);
};
